import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { AuthFormData, AuthErrors, AuthMode } from './types';
import RoleSelector from './components/RoleSelector';
import AuthForm from './components/AuthForm';
import SocialAuth from './components/SocialAuth';
import Icon from '../../components/AppIcon';
import { otpService } from '../../services/otpService';

const Authentication = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, signUp, signInWithProvider, user, userProfile, loading } = useAuth();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<AuthFormData>({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    companyName: '',
    phoneNumber: '',
    phoneOtp: '',
    emailOtp: '',
    role: ''
  });
  const [errors, setErrors] = useState<AuthErrors>({});
  const [successMessage, setSuccessMessage] = useState('');
  
  // Phone OTP states
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [phoneOtpSent, setPhoneOtpSent] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);

  // Email OTP states
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [sendingEmailOtp, setSendingEmailOtp] = useState(false);
  const [verifyingEmailOtp, setVerifyingEmailOtp] = useState(false);

  // Get redirect URL from query params
  const searchParams = new URLSearchParams(location.search);
  const redirectUrl = searchParams.get('redirect');

  // Redirect already logged-in users
  useEffect(() => {
    if (!loading && user && userProfile) {
      // If there's a redirect URL, go there instead of dashboard
      if (redirectUrl) {
        navigate(redirectUrl, { replace: true });
        return;
      }
      const dashboardRoutes: Record<string, string> = {
        brand: '/brand-dashboard',
        creator: '/creator-dashboard',
        admin: '/admin-dashboard',
        agency: '/agency-dashboard',
      };
      const redirectTo = dashboardRoutes[userProfile.role] || '/';
      navigate(redirectTo, { replace: true });
    }
  }, [user, userProfile, loading, navigate, redirectUrl]);

  const authModes: Record<'login' | 'register', AuthMode> = {
    login: {
      type: 'login',
      title: 'Welcome to NOONX',
      subtitle: 'Sign in to connect with top brands and creators',
      submitText: 'Sign In',
      toggleText: "Don't have an account? Sign up"
    },
    register: {
      type: 'register',
      title: 'Join NOONX',
      subtitle: 'Create your account and start your journey',
      submitText: 'Create Account',
      toggleText: 'Already have an account? Sign in'
    }
  };

  useEffect(() => {
    const state = location.state as { role?: 'brand' | 'creator' | 'agency' | 'admin'; mode?: 'login' | 'register' } | null;
    if (state?.role) {
      setFormData((prev) => ({ ...prev, role: state.role || '' }));
    }
    if (state?.mode) {
      setMode(state.mode);
    }
  }, [location.state]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof AuthErrors]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
    // Reset phone verification if phone number changes
    if (name === 'phoneNumber') {
      setPhoneVerified(false);
      setPhoneOtpSent(false);
      setFormData((prev) => ({ ...prev, phoneOtp: '' }));
      // Clear Firebase phone auth state
      otpService.clearPhoneAuthState();
    }
    // Reset email verification if email changes
    if (name === 'email') {
      setEmailVerified(false);
      setEmailOtpSent(false);
      setFormData((prev) => ({ ...prev, emailOtp: '' }));
    }
  };

  const handleRoleSelect = (role: 'brand' | 'creator' | 'agency' | 'admin') => {
    setFormData((prev) => ({ ...prev, role }));
    setErrors((prev) => ({ ...prev, role: '' }));
  };

  // Send OTP to phone number
  const handleSendPhoneOtp = async () => {
    if (!formData.phoneNumber) {
      setErrors((prev) => ({ ...prev, phoneNumber: 'Please enter a phone number' }));
      return;
    }

    // Validate phone format (must start with + and country code)
    const phoneRegex = /^\+[1-9]\d{9,14}$/;
    const cleanPhone = formData.phoneNumber.replace(/\s/g, '');
    
    if (!phoneRegex.test(cleanPhone)) {
      setErrors((prev) => ({ 
        ...prev, 
        phoneNumber: 'Please enter a valid phone number with country code (e.g., +91xxxxxxxxxx)' 
      }));
      return;
    }

    setSendingOtp(true);
    setErrors((prev) => ({ ...prev, phoneNumber: '', phoneOtp: '' }));

    try {
      const result = await otpService.sendPhoneOtp(cleanPhone);
      
      if (result.success) {
        setPhoneOtpSent(true);
        setSuccessMessage(result.message || 'OTP sent to your phone!');
      } else {
        setErrors((prev) => ({ ...prev, phoneNumber: result.error || 'Failed to send OTP' }));
      }
    } catch (error: any) {
      setErrors((prev) => ({ ...prev, phoneNumber: error.message || 'Failed to send OTP' }));
    } finally {
      setSendingOtp(false);
    }
  };

  // Verify phone OTP
  const handleVerifyPhoneOtp = async () => {
    if (!formData.phoneOtp || formData.phoneOtp.length !== 6) {
      setErrors((prev) => ({ ...prev, phoneOtp: 'Please enter a valid 6-digit OTP' }));
      return;
    }

    setVerifyingOtp(true);
    setErrors((prev) => ({ ...prev, phoneOtp: '' }));

    try {
      const cleanPhone = formData.phoneNumber?.replace(/\s/g, '') || '';
      const result = await otpService.verifyPhoneOtp(cleanPhone, formData.phoneOtp);
      
      if (result.success) {
        setPhoneVerified(true);
        setSuccessMessage('Phone number verified successfully!');
      } else {
        setErrors((prev) => ({ ...prev, phoneOtp: result.error || 'Invalid OTP' }));
      }
    } catch (error: any) {
      setErrors((prev) => ({ ...prev, phoneOtp: error.message || 'Verification failed' }));
    } finally {
      setVerifyingOtp(false);
    }
  };

  // Send OTP to email
  const handleSendEmailOtp = async () => {
    if (!formData.email) {
      setErrors((prev) => ({ ...prev, email: 'Please enter an email address' }));
      return;
    }

    // Validate email format
    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      setErrors((prev) => ({ ...prev, email: 'Please enter a valid email address' }));
      return;
    }

    setSendingEmailOtp(true);
    setErrors((prev) => ({ ...prev, email: '', emailOtp: '' }));

    try {
      const result = await otpService.sendEmailOtp(formData.email);
      
      if (result.success) {
        setEmailOtpSent(true);
        setSuccessMessage(result.message || 'OTP sent to your email!');
      } else {
        setErrors((prev) => ({ ...prev, email: result.error || 'Failed to send OTP' }));
      }
    } catch (error: any) {
      setErrors((prev) => ({ ...prev, email: error.message || 'Failed to send OTP' }));
    } finally {
      setSendingEmailOtp(false);
    }
  };

  // Verify email OTP
  const handleVerifyEmailOtp = async () => {
    if (!formData.emailOtp || formData.emailOtp.length !== 6) {
      setErrors((prev) => ({ ...prev, emailOtp: 'Please enter a valid 6-digit OTP' }));
      return;
    }

    setVerifyingEmailOtp(true);
    setErrors((prev) => ({ ...prev, emailOtp: '' }));

    try {
      const result = await otpService.verifyEmailOtp(formData.email, formData.emailOtp);
      
      if (result.success) {
        setEmailVerified(true);
        setSuccessMessage('Email verified successfully!');
      } else {
        setErrors((prev) => ({ ...prev, emailOtp: result.error || 'Invalid OTP' }));
      }
    } catch (error: any) {
      setErrors((prev) => ({ ...prev, emailOtp: error.message || 'Verification failed' }));
    } finally {
      setVerifyingEmailOtp(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: AuthErrors = {};

    // Role is only required for registration, not login
    if (mode === 'register' && !formData.role) {
      newErrors.role = 'Please select a role';
    }

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    } else if (mode === 'register' && !emailVerified) {
      newErrors.email = 'Please verify your email';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (mode === 'register') {
      if (!formData.fullName) {
        newErrors.fullName = 'Full name is required';
      }

      if (formData.role === 'brand' && !formData.companyName) {
        newErrors.companyName = 'Company name is required';
      }

      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }

      // Phone verification is optional - just validate format if provided
      if (formData.phoneNumber && !/^\+[1-9]\d{9,14}$/.test(formData.phoneNumber.replace(/\s/g, ''))) {
        newErrors.phoneNumber = 'Please enter a valid phone number with country code (e.g., +91xxxxxxxxxx)';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});
    setSuccessMessage('');

    try {
      if (mode === 'login') {
        const { error, profile } = await signIn(formData.email, formData.password);

        if (error) {
          setErrors({ submit: error.message || 'Invalid credentials. Please try again.' });
          return;
        }

        if (!profile) {
          setErrors({ submit: 'Profile not found. Please contact support.' });
          return;
        }

        setSuccessMessage('✅ Login successful! Redirecting...');

        setTimeout(() => {
          // If there's a redirect URL, go there instead of dashboard
          if (redirectUrl) {
            navigate(redirectUrl);
            return;
          }
          const dashboardRoutes = {
            brand: '/brand-dashboard',
            creator: '/creator-dashboard',
            admin: '/admin-dashboard'
          };
          navigate(dashboardRoutes[profile.role as keyof typeof dashboardRoutes] || '/');
        }, 1000);
      } else {
        const userData = {
          email: formData.email,
          full_name: formData.fullName,
          role: formData.role,
          company_name: formData.companyName || null,
          phone_number: formData.phoneNumber?.replace(/\s/g, '') || null,
          phone_verified: phoneVerified,
          email_verified: emailVerified
        };

        const result = await signUp(formData.email, formData.password, userData);

        if (result.error) {
          setErrors({ submit: result.error.message || 'Registration failed. Please try again.' });
          return;
        }

        if (!result.profile) {
          setErrors({ submit: 'Account created but profile setup failed. Please contact support.' });
          return;
        }

        // Check if email confirmation is required
        if (result.message) {
          setSuccessMessage(result.message);
          // Don't auto-switch to login, user needs to confirm email first
          return;
        }

        setSuccessMessage('✅ Account created successfully! Redirecting to login...');

        // Auto switch to login after successful signup
        setTimeout(() => {
          setMode('login');
          setSuccessMessage('Account created! Please sign in to continue.');
        }, 2000);
      }
    } catch (error: any) {
      setErrors({
        submit: error?.message || 'An unexpected error occurred. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialAuth = async (provider: 'email' | 'google' | 'apple') => {
    setIsLoading(true);
    setErrors({});

    try {
      if (provider === 'email') {
        // Focus the email field in the form
        const emailInput = document.getElementsByName('email')[0];
        if (emailInput) {
          emailInput.focus();
        }
        setIsLoading(false);
        return;
      }
      
      const { error } = await signInWithProvider(provider);
      if (error) {
        setErrors({ submit: error.message || `${provider} authentication failed` });
      }
    } catch (error: any) {
      setErrors({ submit: error?.message || `${provider} authentication failed` });
    } finally {
      setIsLoading(false);
    }
  };

  const handleModeToggle = () => {
    setMode(mode === 'login' ? 'register' : 'login');
    setErrors({});
    setSuccessMessage('');
    
    // Reset form data, but keep email if switching modes
    const currentEmail = formData.email;
    setFormData({
      email: currentEmail,
      password: '',
      confirmPassword: '',
      fullName: '',
      companyName: '',
      phoneNumber: '',
      phoneOtp: '',
      emailOtp: '',
      role: ''
    });
    // Reset phone verification states
    setPhoneVerified(false);
    setPhoneOtpSent(false);
    // Reset email verification states
    setEmailVerified(false);
    setEmailOtpSent(false);
    // Clear Firebase phone auth state
    otpService.clearPhoneAuthState();
  };

  const currentMode = authModes[mode];

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 -left-4 w-72 h-72 bg-primary rounded-full mix-blend-screen filter blur-3xl animate-blob"></div>
        <div className="absolute top-0 -right-4 w-72 h-72 bg-primary-light rounded-full mix-blend-screen filter blur-3xl animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-72 h-72 bg-primary rounded-full mix-blend-screen filter blur-3xl animate-blob animation-delay-4000"></div>
      </div>

      {/* Main Content */}
      <div className="w-full max-w-md relative z-10">
        <div className="rounded-2xl shadow-2xl p-8 border border-dark-700 bg-dark-800 backdrop-blur-xl">
          {/* Logo and Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center mb-4">
              <img
                src="/assets/images/Noonx_logo_small.png"
                alt="NOONX Platform Logo - Modern influencer marketing platform connecting brands with creators"
                className="h-16 w-auto object-contain"
                loading="eager"
                fetchpriority="high"
                width="221"
                height="60"
              />

            </div>
            <h1 className="text-3xl font-bold mb-2 text-white">{currentMode.title}</h1>
            <p className="text-[rgba(253,252,252,1)]">{currentMode.subtitle}</p>
          </div>

          {/* Show role selector only for registration, or if role not selected during registration */}
          {(mode === 'register' && !formData.role) ?
          <RoleSelector
            selectedRole={formData.role}
            onRoleSelect={handleRoleSelect}
            error={errors.role} /> :


          <>
              {/* Selected Role Badge - Only show during registration */}
              {mode === 'register' && formData.role &&
              <div className="mb-6 p-4 bg-dark-700 rounded-xl border border-dark-600 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center shadow-lg shadow-primary/30">
                    <Icon name="User" size={20} className="text-white" />
                  </div>
                  <span className="text-base font-semibold text-white capitalize">
                    {formData.role}
                  </span>
                </div>
                <button
                type="button"
                onClick={() => setFormData((prev) => ({ ...prev, role: '' }))}
                className="text-sm font-medium text-primary hover:text-primary-light transition-colors"
                disabled={isLoading}>

                  Change
                </button>
              </div>
              }

              <AuthForm
              mode={mode}
              formData={formData}
              errors={errors}
              isLoading={isLoading}
              onInputChange={handleInputChange}
              onSubmit={handleSubmit}
              onModeToggle={handleModeToggle}
              successMessage={successMessage}
              phoneVerified={phoneVerified}
              phoneOtpSent={phoneOtpSent}
              onSendPhoneOtp={handleSendPhoneOtp}
              onVerifyPhoneOtp={handleVerifyPhoneOtp}
              sendingOtp={sendingOtp}
              verifyingOtp={verifyingOtp}
              emailVerified={emailVerified}
              emailOtpSent={emailOtpSent}
              onSendEmailOtp={handleSendEmailOtp}
              onVerifyEmailOtp={handleVerifyEmailOtp}
              sendingEmailOtp={sendingEmailOtp}
              verifyingEmailOtp={verifyingEmailOtp} />


              <div className="mt-6">
                <SocialAuth isLoading={isLoading} onSocialAuth={handleSocialAuth} />
              </div>
            </>
          }

          {/* Footer Links */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-center space-x-4 text-xs text-gray-500">
              <button
                type="button"
                onClick={() => navigate('/landing-page')}
                className="hover:text-[#E42313] transition-all font-medium">

                Back to Home
              </button>
              <span>•</span>
              <button type="button" onClick={() => navigate('/help-center')} className="hover:text-[#E42313] transition-all font-medium">
                Help Center
              </button>
              <span>•</span>
              <button type="button" onClick={() => navigate('/privacy')} className="hover:text-[#E42313] transition-all font-medium">
                Privacy
              </button>
            </div>
          </div>
        </div>

        {/* Terms and Conditions */}
        <div className="mt-4 text-center text-xs text-gray-400 border-white">
          <p className="text-neutral-50">Protected by reCAPTCHA and subject to the NOONX</p>
          <p className="mt-1">
            <button type="button" onClick={() => navigate('/privacy-policy')} className="hover:text-white transition-all">
              Privacy Policy
            </button>
            {' and '}
            <button type="button" onClick={() => navigate('/terms-of-service')} className="hover:text-white transition-all">
              Terms of Service
            </button>
          </p>
        </div>
      </div>
    </div>);

};

export default Authentication;